// AnmeldeBestaetigung.jsx — Bestätigung nach dem Absenden der Anmeldung (25.10.2026)
//
// Vorher: ein grüner Streifen „N Wettkämpfer angemeldet“, der nach Sekunden
// verschwand, und kein „Wie geht es weiter“ (Befund 24_anmeldungen).
// Jetzt eine eigene Ansicht im Vereinsbereich: was angemeldet ist (je Sportler
// die Startklassen mit Namen, Code klein), Startgeld, nächste Schritte
// (Bezahlen, Änderungen bis wann, Kontakt) und „Weitere Sportler anmelden“.
//
// Zahlen kommen aus der frisch geladenen Anmeldeliste (GET /api/anmeldungen/
// verein/:id), Fristen und Kontakt aus der Ausschreibung (GET /api/turniere/
// public/:id/info). Gerechnet wird hier nichts außer der Summe.
import React from 'react';
import ZahlungKasten from './ZahlungKasten';
import { deutscherName } from '../../lib/klassenEinfach';

const euro = (b) => {
  const n = parseFloat(b);
  return Number.isFinite(n) ? n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '–';
};
const datum = (d) => {
  if (!d) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? null : x.toLocaleDateString('de-DE');
};

export default function AnmeldeBestaetigung({ daten, anmeldungen, vereinsId, headers, onWeitere, onUebersicht }) {
  const t = daten.turnier || {};
  const tid = t.turnier_id || t.id;
  const info = daten.info || {};
  // Deutscher Name vorne, Code klein (lib/klassenEinfach.js, 25.10.2026)
  const namen = new Map((info.divisionen || []).map((d) => [d.code, deutscherName({ Division_Name: d.name, Division_Type: d.typ, Division_Code: d.code })]));
  const ids = new Set(daten.wkIds || []);

  // Alle aktiven Starts dieses Turniers; die eben angemeldeten Sportler zuerst
  const zeilen = anmeldungen.filter((a) => Number(a.turnier_id) === Number(tid) && a.status !== 'Storniert');
  const jeSportler = new Map();
  for (const a of zeilen) {
    const id = Number(a.wettkaempfer_id || a.wettkampfer_id);
    if (!jeSportler.has(id)) jeSportler.set(id, { id, name: `${a.vorname} ${a.nachname}`, starts: [], neu: ids.has(id) });
    jeSportler.get(id).starts.push(a);
  }
  const sportler = [...jeSportler.values()].sort((a, b) => Number(b.neu) - Number(a.neu) || a.name.localeCompare(b.name, 'de'));
  const neuAnzahl = sportler.filter((s) => s.neu).length;
  const summe = zeilen.reduce((s, a) => s + (parseFloat(a.startgebuehr) || 0), 0);
  const offen = zeilen.filter((a) => !a.bezahlt).reduce((s, a) => s + (parseFloat(a.startgebuehr) || 0), 0);

  const schluss = datum(info.anmeldeschluss_iso || t.anmeldeschluss);
  const kontaktMail = info.kontakt_email;
  const kontaktTel = info.kontakt_telefon;

  return (
    <section className="vd-best" aria-labelledby="vd-best-titel">
      <div className="vd-best-kopf">
        <span className="vd-best-haken" aria-hidden="true">✓</span>
        <div>
          <h2 id="vd-best-titel">Anmeldung gespeichert</h2>
          <p>
            {neuAnzahl > 0 ? `${neuAnzahl} Sportler angemeldet für ` : 'Angemeldet für '}
            <strong>{t.name}</strong>{datum(t.datum) ? ` am ${datum(t.datum)}` : ''}{t.ort ? ` in ${t.ort}` : ''}.
            Eine Bestätigung findest du jederzeit unter „Anmeldungen“.
          </p>
        </div>
      </div>

      <div className="vd-card">
        <div className="vd-card-header"><h3>Was angemeldet ist</h3><span className="vd-badge vd-badge--gold">{zeilen.length} {zeilen.length === 1 ? 'Start' : 'Starts'}</span></div>
        {sportler.length === 0 ? (
          <p className="vd-hint">Die Liste wird gerade geladen. Unter „Anmeldungen“ steht alles.</p>
        ) : (
          <ul className="vd-best-liste">
            {sportler.map((s) => (
              <li key={s.id}>
                <div className="vd-best-name">{s.name}{s.neu && <span className="vd-best-neu">neu</span>}</div>
                <ul>
                  {s.starts.map((a) => (
                    <li key={a.anmeldung_id || `${s.id}-${a.division_code}`}>
                      <span className="vd-best-klasse">
                        {a.division_code ? (namen.get(a.division_code) || a.division_code) : 'Vorgemerkt, Startklasse fehlt noch'}
                        {a.division_code && namen.get(a.division_code) && <small> {a.division_code}</small>}
                        {a.team_name && <small> · Team {a.team_name}</small>}
                      </span>
                      <span className="vd-best-geld">{euro(a.startgebuehr)}{a.bezahlt ? ' ✓' : ''}</span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
        <div className="vd-best-summe"><span>Startgeld für dieses Turnier</span><strong>{euro(summe)}</strong></div>
      </div>

      <div className="vd-card">
        <div className="vd-card-header"><h3>So geht es weiter</h3></div>
        <ol className="vd-best-schritte">
          <li>
            <strong>Startgeld bezahlen</strong>
            <div>
              {offen > 0
                ? <>Offen sind <strong>{euro(offen)}</strong>.{info.zahlungsfrist ? ` Zahlungsfrist: ${info.zahlungsfrist}.` : ''}{info.zahlungsweise ? ` ${info.zahlungsweise}` : ''}</>
                : 'Für dieses Turnier ist nichts offen.'}
            </div>
            {offen > 0 && vereinsId && <ZahlungKasten turnierId={tid} vereinsId={vereinsId} headers={headers} />}
          </li>
          <li>
            <strong>Ändern oder nachmelden</strong>
            <div>
              {schluss ? `Bis zum Anmeldeschluss am ${schluss} ` : 'Bis zum Anmeldeschluss '}
              änderst du die Anmeldung über denselben Knopf „Anmeldung ergänzen“ unter „Veranstaltungen“, abmelden geht unter „Anmeldungen“.
              {info.nachmeldung_bis_text ? ` Nachmelden bis ${info.nachmeldung_bis_text}.` : ''}
              {info.abmelde_regel ? ` Abmeldung: ${info.abmelde_regel}` : ''}
            </div>
          </li>
          <li>
            <strong>Fragen zum Turnier</strong>
            <div>
              {kontaktMail || kontaktTel ? (
                <>Der Veranstalter hilft weiter: {kontaktMail && <a href={`mailto:${kontaktMail}`}>{kontaktMail}</a>}
                  {kontaktMail && kontaktTel ? ' · ' : ''}{kontaktTel && <a href={`tel:${String(kontaktTel).replace(/\s/g, '')}`}>{kontaktTel}</a>}</>
              ) : (
                <>Die Kontaktdaten stehen in der <a href={`/event/${tid}`}>Ausschreibung</a>.</>
              )}
            </div>
          </li>
        </ol>
      </div>

      <div className="vd-best-knoepfe">
        <button className="vd-btn vd-btn--haupt" onClick={onWeitere}>Weitere Sportler anmelden</button>
        <button className="vd-btn vd-btn--ghost" onClick={onUebersicht}>Zu meinen Anmeldungen</button>
      </div>
    </section>
  );
}
